import React, { useState, useEffect } from 'react';
import { Edit, Plus, AlertTriangle, Check, X, MessageSquare, FileText, Settings } from 'lucide-react';
import Sidebar from '../../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../../component/b2c/navBar/navBar';
import { useMyContext } from '../../../../../context/MyContext';
import { IndianRupee } from "lucide-react";
import toast from 'react-hot-toast';

const WhatsAppRatesManager = () => {
    const token = localStorage.getItem("token");
    const cus_id = localStorage.getItem("cus_id");
    const { isDarkMode, isToggleSidebar } = useMyContext();
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        message_type: '',
        rate: '',
        description: '',
        status: 'Active'
    });
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertConfig, setAlertConfig] = useState({});

    useEffect(() => {
        fetchRates();
    }, []);

    const fetchRates = async () => {
        try {
            setLoading(true);

            const response = await fetch("https://api.pickupxpress.com/admin/whatsapp/rates", {
                method: "GET",
                headers: {
                    cus_id: cus_id,
                    "Content-Type": "application/json",
                    token: token,
                },
            });

            const data = await response.json();

            if (data.status === true) {
                setRates(data?.data);
            } else {
                toast.error(data.message || "Something went wrong");
            }
        } catch (err) {
            toast.error("Failed to fetch rates");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(
                "https://api.pickupxpress.com/admin/whatsapp/setRate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        cus_id: cus_id,
                        token: token,
                    },
                    body: JSON.stringify({
                        message_type: formData.message_type,
                        rate: formData.rate,
                        description: formData.description,
                        status: 'Active'
                    }),
                }
            );

            const data = await response.json();

            if (data.status === true) {
                if (editingId) {
                    setRates((prev) =>
                        prev.map((rate) =>
                            rate.id === editingId ? { ...formData, id: editingId } : rate
                        )
                    );
                    showCustomAlert("Rate updated successfully!", "success");
                } else {
                    const newRate = { ...formData, id: data?.insertedId || Date.now() };
                    setRates((prev) => [...prev, newRate]);
                    showCustomAlert("Rate created successfully!", "success");
                }
                resetForm();
            } else {
                showCustomAlert(data.message || "Failed to save rate", "error");
            }
        } catch (err) {
            console.error(err);
            showCustomAlert("Something went wrong while saving the rate", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = (rate, newStatus) => {
        setAlertConfig({
            title: `${newStatus === "Active" ? "Activate" : "Deactivate"} Rate`,
            message: `Are you sure you want to ${newStatus === "Active" ? "activate" : "deactivate"} this rate for "${rate.message_type}"?`,
            confirmText: newStatus === "Active" ? "Activate" : "Deactivate",
            type: "confirm",
            onConfirm: () => updateStatus(rate, newStatus),
        });
        setShowAlert(true);
    };

    const updateStatus = async (rate, newStatus) => {
        setLoading(true);

        try {
            const response = await fetch(
                "https://api.pickupxpress.com/admin/whatsapp/setRate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        cus_id: cus_id,
                        token: token,
                    },
                    body: JSON.stringify({
                        id: rate.id,
                        message_type: rate.message_type,
                        rate: rate.rate,
                        description: rate.description,
                        status: newStatus,
                    }),
                }
            );

            const data = await response.json();

            if (data.status === true) {
                setRates((prev) =>
                    prev.map((r) =>
                        r.id === rate.id ? { ...r, status: newStatus } : r
                    )
                );

                showCustomAlert(
                    `Rate ${newStatus === "Active" ? "activated" : "deactivated"} successfully!`,
                    "success"
                );
            } else {
                showCustomAlert(data.message || "Failed to update status", "error");
            }
        } catch (err) {
            console.error(err);
            showCustomAlert("Something went wrong while updating status", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (rate) => {
        setFormData({
            message_type: rate.message_type,
            rate: rate.rate,
            description: rate.description,
        });
        setEditingId(rate.id);
        setShowForm(true);
    };

    const resetForm = () => {
        setFormData({
            message_type: '',
            rate: '',
            description: '',
            status: 'Active'
        });
        setEditingId(null);
        setShowForm(false);
    };

    const showCustomAlert = (message, type) => {
        setAlertConfig({
            title: type === 'success' ? 'Success' : 'Error',
            message,
            type,
            onConfirm: () => setShowAlert(false)
        });
        setShowAlert(true);
    };

    // Custom Alert Component — compact, single accent, no gradients
    const CustomAlert = ({ config, onClose }) => {
        if (!config) return null;

        return (
            <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                <div className="bg-white dark:bg-darkmodeColor rounded-xl max-w-sm w-full mx-4 shadow-xl border border-slate-100 dark:border-slate-700">
                    <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                            {config.type === 'success' && (
                                <div className="bg-emerald-50 p-2 rounded-lg shrink-0">
                                    <Check className="w-5 h-5 text-emerald-600" />
                                </div>
                            )}
                            {config.type === 'error' && (
                                <div className="bg-red-50 p-2 rounded-lg shrink-0">
                                    <X className="w-5 h-5 text-red-600" />
                                </div>
                            )}
                            {config.type === 'confirm' && (
                                <div className="bg-amber-50 p-2 rounded-lg shrink-0">
                                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                                </div>
                            )}
                            <h3 className="text-base font-semibold text-slate-900 dark:text-white">{config.title}</h3>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-300 mb-5 leading-relaxed">{config.message}</p>
                        <div className="flex justify-end gap-2">
                            {config.type === 'confirm' && (
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    if (config.onConfirm) config.onConfirm();
                                    onClose();
                                }}
                                className={`px-4 py-2 text-sm rounded-lg transition-colors font-medium text-white ${config.type === 'success'
                                    ? 'bg-emerald-600 hover:bg-emerald-700'
                                    : config.type === 'error'
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-indigo-600 hover:bg-indigo-700'
                                    }`}
                            >
                                {config.confirmText || 'OK'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Add / Edit Rate — now a modal popup instead of an inline card
    const RateFormModal = () => {
        if (!showForm) return null;

        return (
            <div
                className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                onClick={resetForm}
            >
                <div
                    className={`rounded-2xl w-full max-w-lg shadow-2xl  ${isDarkMode ? '' : 'bg-white border-slate-200'}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Modal header */}
                    <div className="flex items-center justify-between bg-gray-200 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                        <div className="flex items-center gap-2.5">
                            <div className="bg-indigo-50 p-1.5 rounded-lg">
                                <Settings className="w-4 h-4 text-indigo-600" />
                            </div>
                            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                                {editingId ? 'Edit Rate' : 'Add New Rate'}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Modal body */}
                    <div className="p-5 space-y-4">
                        <div>
                            <label className="flex items-center text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                <MessageSquare className="w-3.5 h-3.5 mr-1.5 text-indigo-500" />
                                Message Type
                            </label>
                            <input
                                type="text"
                                value={formData.message_type}
                                onChange={(e) => setFormData({ ...formData, message_type: e.target.value })}
                                className={`w-full px-3 py-1 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-colors outline-none ${isDarkMode ? "dark:bg-darkmodeColor dark:border-slate-600" : "bg-white border-slate-200"}`}
                                placeholder="e.g., shipment_ofd"
                                required
                            />
                        </div>

                        <div>
                            <label className="flex items-center text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                <IndianRupee className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />
                                Rate
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.rate}
                                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                className={`w-full px-3 py-1 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-colors outline-none ${isDarkMode ? "dark:bg-darkmodeColor dark:border-slate-600" : "bg-white border-slate-200"}`}
                                placeholder="e.g., 2.30"
                                required
                            />
                        </div>

                        <div>
                            <label className="flex items-center text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                <FileText className="w-3.5 h-3.5 mr-1.5 text-violet-500" />
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className={`w-full px-3 py-1 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-colors outline-none resize-none ${isDarkMode ? "dark:bg-darkmodeColor dark:border-slate-600" : "bg-white border-slate-200"}`}
                                rows="3"
                                placeholder="Enter a detailed description..."
                                required
                            />
                        </div>
                    </div>

                    {/* Modal footer */}
                    <div className="flex justify-end gap-2 px-5 pb-2 border-slate-100 dark:border-slate-700">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading || !formData.rate || !formData.description || !formData.message_type}
                            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-sm"
                        >
                            {loading ? 'Saving...' : editingId ? 'Update Rate' : 'Create Rate'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Compact Switch Component
    const StatusSwitch = ({ checked, onChange, disabled = false }) => (
        <label className="inline-flex items-center gap-2 cursor-pointer group">
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                className="sr-only peer"
            />
            <div className={`
                relative w-10 h-[22px] rounded-full transition-colors duration-200
                ${checked ? 'bg-emerald-500' : 'bg-slate-300'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                peer-focus:ring-2 peer-focus:ring-offset-1 peer-focus:ring-indigo-400
            `}>
                <div className={`
                    absolute top-0.5 left-0.5 w-[18px] h-[18px] bg-white rounded-full shadow
                    transform transition-transform duration-200
                    ${checked ? 'translate-x-[18px]' : 'translate-x-0'}
                `} />
            </div>
            <span className={`text-xs font-medium ${checked ? 'text-emerald-600' : 'text-slate-400'}`}>
                {checked ? 'Active' : 'Inactive'}
            </span>
        </label>
    );

    return (
        <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-slate-50'}`}>
            <div className="z-10">
                <NavBar />
            </div>

            <div className="flex flex-1 overflow-hidden">
                {isToggleSidebar && (
                    <div className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
                        style={
                            window.innerWidth < 768
                                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                                : {}
                        }>
                        <Sidebar />
                    </div>
                )}
                <div className={`min-h-screen w-full overflow-auto ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-gray-200'}`}>
                    <div className={`max-w-6xl overflow-auto mx-auto px-4 sm:px-6 py-6 ${isDarkMode ? 'dark:bg-darkmodeColor dark:text-white' : ''}`}>

                        {/* Header — compact, single accent */}
                        <div className={`flex items-center justify-between mb-5 ${isDarkMode ? 'text-white' : ''}`}>
                            <div className="flex items-center gap-3">
                                <div className="bg-indigo-600 p-2.5 rounded-xl shadow-sm">
                                    <MessageSquare className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-slate-900 dark:text-white leading-tight">
                                        WhatsApp Rates
                                    </h1>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Manage your messaging rates</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowForm(true)}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors font-medium shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Add Rate
                            </button>
                        </div>

                        {/* Table — compact card */}
                        <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor dark:border-slate-700' : 'bg-white border-slate-200'}`}>
                            <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-700">
                                <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Current Rates</h2>
                            </div>

                            {loading && rates.length === 0 ? (
                                <div className="p-10 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent mx-auto"></div>
                                    <p className="mt-3 text-sm text-slate-500">Loading rates...</p>
                                </div>
                            ) : (
                                <div className="overflow-y-auto max-h-96">
                                    <table className="min-w-full">
                                        <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 z-10">
                                            <tr>
                                                <th className="dark:text-white px-5 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                                    Message Type
                                                </th>
                                                <th className="dark:text-white px-5 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                                    Rate
                                                </th>
                                                <th className="dark:text-white px-5 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                                    Description
                                                </th>
                                                <th className="dark:text-white px-5 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                                    Status
                                                </th>
                                                <th className="dark:text-white px-5 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                            {rates.map((rate, index) => (
                                                <tr key={rate.id || index} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                    <td className="px-5 py-3 whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            <div className="bg-indigo-50 p-1.5 rounded-md">
                                                                <MessageSquare className="w-3.5 h-3.5 text-indigo-600" />
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-800 dark:text-white">
                                                                {rate.message_type}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-3 whitespace-nowrap">
                                                        <span className="text-sm font-semibold text-slate-800 dark:text-white">
                                                            ₹{rate.rate}
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-3">
                                                        <div className="text-sm text-slate-500 dark:text-slate-300 max-w-xs truncate">
                                                            {rate.description}
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-3 whitespace-nowrap">
                                                        <StatusSwitch
                                                            checked={rate.status === 'Active'}
                                                            onChange={(e) => {
                                                                const newStatus = e.target.checked ? 'Active' : 'Inactive';
                                                                handleStatusToggle(rate, newStatus);
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-5 py-3 whitespace-nowrap">
                                                        <button
                                                            onClick={() => handleEdit(rate)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 transition-colors font-medium"
                                                        >
                                                            <Edit className="w-3.5 h-3.5" />
                                                            Edit
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {rates.length === 0 && !loading && (
                                                <tr>
                                                    <td colSpan="5" className="px-5 py-10 text-center">
                                                        <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                                        <p className="text-sm text-slate-500">No rates found</p>
                                                        <p className="text-xs text-slate-400">Add your first rate to get started</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add / Edit Rate Modal */}
            <RateFormModal />

            {/* Custom Alert */}
            {showAlert && (
                <CustomAlert
                    config={alertConfig}
                    onClose={() => setShowAlert(false)}
                />
            )}
        </div>
    );
};
export default WhatsAppRatesManager;